import { CCol, CInput, CRow, CTextarea } from "@coreui/react"
import classNames from "classnames"
import ToasterProvider from "components/common/Context/ToasterContext"
import useNotifications from "components/common/customHooks/useNotifications"
import { OptionDropdown } from "components/common/dropdown"
import { FileAttachment } from "components/common/forms"
import { LoadingSpinnerOverlay } from "components/common/loading"
import { ConfirmModal } from "components/common/modal"
import { postServiceRequest } from "extensions/usoc/api/service"
import React, { useContext, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { checkIfFileSupported, useExtension } from "utils"
import { useUserAuth } from "utils/hooks"

import FormActions from "./FormActions"

const urgencyOptions = [
  { text: "High", value: "1", key: "1" },
  { text: "Medium", value: "2", key: "2" },
  { text: "Low", value: "3", key: "3" }
]

export default function RequestServiceForm() {
  const auth = useUserAuth()
  const projectID = useSelector((state) => {
    return state.settings.currentProject.id
  })

  const extension = useExtension()
  const fileInputRef = useRef(null)
  const host = extension?.config?.env?.clientApiGateway?.URL

  const [account, setAccount] = useState("")
  const [urgency, setUrgency] = useState("3")
  const [shortDescription, setShortDescription] = useState("")
  const [longDescription, setLongDescription] = useState("")

  const [showModal, setShowModal] = useState(false)
  const [createdSrNumber, setCreatedSrNumber] = useState("")

  const [isShortDescriptionTouched, setIsShortDescriptionTouched] =
    useState(false)
  const [isLongDescriptionTouched, setIsLongDescriptionTouched] =
    useState(false)
  const [uploadFile, setUploadFile] = useState([])
  const [isUploadFileTouched, setIsUploadFileTouched] = useState(false)

  const [notifications, setNotifications] = useNotifications()
  const [isLoading, setIsLoading] = useState(false)

  const accounts = useSelector((state) => {
    return state.user.accounts?.account_attribute || []
  })

  const onOk = () => {
    history.push({
      pathname: "/service",
      search: `?service_number=${createdSrNumber}`
    })
  }

  const history = useHistory()
  const enqueueToast = useContext(ToasterProvider)
  const postUploadFile = async (fileDataURL) => {
    const promises = []
    for (const data of fileDataURL.presigned_urls) {
      const options = {
        method: "PUT",
        body: uploadFile.find((item) => item.name === data.filename),
        mode: "cors",
        headers: {
          "Content-Type": ""
        }
      }
      promises.push(
        fetch(data.url, options).catch((err) => {
          throw err
        })
      )
    }
    const results = await Promise.all(promises)
    return results
  }
  const handleSubmit = async () => {
    setIsShortDescriptionTouched(true)
    setIsLongDescriptionTouched(true)
    setIsUploadFileTouched(true)
    if (
      !shortDescription ||
      !longDescription ||
      !checkIfFileSupported(uploadFile)
    ) {
      return
    }
    setIsLoading(true)
    const serviceRequestBody = {
      account_id: account,
      urgency,
      short_description: shortDescription,
      description: longDescription,
      attachments: uploadFile ? uploadFile?.map((data) => data?.name) : []
    }

    const response = await postServiceRequest(
      { auth, host, body: JSON.stringify(serviceRequestBody) },
      projectID
    )
    if (!response.error && response.data) {
      try {
        await postUploadFile(response.data)
        const ticketNumber = response?.data?.ticket_number
        if (ticketNumber) {
          const currentDate = new Date()
          const notification = {
            id: notifications.length + 1,
            from: "Created Successfully!!",
            active: true,
            approved: true,
            ticket_number: response.data.ticket_number,
            time: `${currentDate.toDateString()} ${currentDate.toLocaleTimeString()}`,
            description: "Service requested successfully with number ",
            requestNumber: ["ServiceRequest", response.data.ticket_number]
          }
          setNotifications([notification, ...notifications])
          setCreatedSrNumber(ticketNumber)
        }
        setShowModal(true)
      } catch (err) {
        setIsLoading(false)
        enqueueToast(
          "failure",
          "failed",
          "Failed to upload the File. Please try again later."
        )
      }
      setAccount("")
      setUrgency("3")
      setShortDescription("")
      setLongDescription("")
      setIsLongDescriptionTouched(false)
      setIsShortDescriptionTouched(false)
      setUploadFile([])
      setIsUploadFileTouched(false)
    } else {
      enqueueToast("failure", null, "Could not request this service")
      history.goBack()
    }
    setIsLoading(false)
  }

  const handleCancel = () => {
    history.goBack()
  }

  return (
    <div className="service-request-container">
      <LoadingSpinnerOverlay isLoading={isLoading} spinnerSize="8rem">
        <CRow className="m-2">
          <CCol md="12">
            <p className="heading py-2">
              General Infrastructure Service Request
            </p>
            <p className="sub-heading">
              Use this catalog item to open general service request related to
              cloud infrastructure
            </p>
          </CCol>
        </CRow>
        <CRow className="mx-2 my-4">
          <CCol xs="12" md="6">
            <p className="field-label font-weight-bold">
              Cloud Service Account *
            </p>
            <OptionDropdown
              items={[
                { text: "Not Sure", key: "" },
                ...(accounts
                  ? accounts.map(
                      ({
                        account_number: accountNumber,
                        account_environment: accountEnvironment,
                        account_name: accountName
                      }) => ({
                        text: [accountEnvironment, accountName, accountNumber]
                          .filter(Boolean)
                          .join(" | "),
                        key: accountNumber
                      })
                    )
                  : [])
              ]}
              value={account}
              onChange={setAccount}
              className="w-100"
              buttonClassName="w-100"
            />
          </CCol>
          <CCol xs="12" md="6">
            <p className="field-label font-weight-bold">Urgency *</p>
            <OptionDropdown
              items={urgencyOptions}
              value={urgency}
              onChange={setUrgency}
              className="w-100"
              buttonClassName="w-100"
            />
          </CCol>
        </CRow>
        <CRow className="mx-2 my-4">
          <CCol xs="12" md="6">
            <p className="field-label font-weight-bold">Short Description *</p>
            <CInput
              type="text"
              name="issue"
              placeholder="Please Describe your Issue"
              value={shortDescription}
              onChange={(e) => {
                setIsShortDescriptionTouched(true)
                setShortDescription(e.target.value)
              }}
              className={classNames({
                "is-invalid": isShortDescriptionTouched && !shortDescription
              })}
            />
            <div className="invalid-feedback">This Field is Required</div>
          </CCol>
        </CRow>
        <CRow className="mx-2 my-4">
          <CCol xs="12" md="6">
            <p className="field-label font-weight-bold">Full Description *</p>
            <CTextarea
              type="text"
              name="issue"
              placeholder="Please Describe your Issue"
              value={longDescription}
              onChange={(e) => {
                setIsLongDescriptionTouched(true)
                setLongDescription(e.target.value)
              }}
              className={classNames({
                "is-invalid": isLongDescriptionTouched && !longDescription
              })}
            />
            <div className="invalid-feedback">This Field is Required</div>
          </CCol>
        </CRow>
        <CRow className="mx-2 my-4">
          <FileAttachment
            uploadFile={uploadFile}
            isUploadFileTouched={isUploadFileTouched}
            setUploadFile={setUploadFile}
            setIsUploadFileTouched={setIsUploadFileTouched}
            fileInputRef={fileInputRef}
          />
        </CRow>
        <FormActions onSubmit={handleSubmit} onCancel={handleCancel} />
      </LoadingSpinnerOverlay>
      {ReactDOM.createPortal(
        <ConfirmModal
          show={showModal}
          setShow={setShowModal}
          message={`Service requested successfully${
            createdSrNumber ? ` with number ${createdSrNumber}` : ""
          }`}
          onCancel={handleCancel}
          onOkay={onOk}
          okayBtnText="View Service Request"
        />,
        document.getElementById("status-modal")
      )}
    </div>
  )
}
